import React, { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { MessageSquare, Send, Check } from "lucide-react"
import { smsApi } from "../../services/smsApi"
import { attorneysApi } from "../../services/attorneysApi"
import { useMyContext } from "../../context/Context"
import {
  SMS_TEMPLATE_DEFINITIONS,
  SMS_TEMPLATE_MAP,
  SEVERANCE_REVIEW_EMAIL_LINE,
  type SmsTemplateKey,
} from "../../data/smsTemplates"

interface Attorney {
  name: string
  phone: string
  email: string
}

interface SmsTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  lead: {
    id: string
    name: string
    first_name: string
    phone: string
    assigned_to_attorney?: {
      name: string
      phone: string
      email: string
    } | null
  }
  onSmsSent: (template: string, status: 'success' | 'error', message: string) => void
}

function buildMessage(
  templateKey: SmsTemplateKey,
  attorney: Attorney | null,
  leadId: string
): string {
  const def = SMS_TEMPLATE_MAP[templateKey]
  let body = def.body
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const intakeUrl = `${origin}/intake/${leadId}`
  const reviewUrl = 'https://example.com/review-placeholder'

  body = body
    .replaceAll('{intake_url}', intakeUrl)
    .replaceAll('{review_url}', reviewUrl)
    .replaceAll('{severance_emails}', SEVERANCE_REVIEW_EMAIL_LINE)

  if (def.requiresAttorney && attorney) {
    body = body
      .replaceAll('{attorney_name}', attorney.name)
      .replaceAll('{attorney_phone}', attorney.phone)
      .replaceAll('{attorney_email}', attorney.email)
  }

  return body
}

export default function SmsTemplateModal({
  isOpen,
  onClose,
  lead,
  onSmsSent
}: SmsTemplateModalProps) {
  const { user } = useMyContext()
  const [attorneys, setAttorneys] = useState<Attorney[]>([])
  const [loadingAttorneys, setLoadingAttorneys] = useState(false)
  const [selectedAttorney, setSelectedAttorney] = useState<string>("")
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplateKey>("UNANSWERED")
  const [sending, setSending] = useState(false)
  const [previewMessage, setPreviewMessage] = useState("")
  const [previewTouched, setPreviewTouched] = useState(false)

  const selectedDef = SMS_TEMPLATE_MAP[selectedTemplate]
  const needsAttorney = selectedDef.requiresAttorney

  useEffect(() => {
    if (!isOpen) return

    const fetchAttorneys = async () => {
      setLoadingAttorneys(true)
      try {
        const response = await attorneysApi.getAttorneys(1, 10)
        const attorneysList: Attorney[] = response.attorneys
          .filter(attorney => attorney.can_send_sms && attorney.phone && attorney.email)
          .map(attorney => ({
            name: attorney.name,
            phone: attorney.phone,
            email: attorney.email
          }))

        setAttorneys(attorneysList)

        if (attorneysList.length > 0) {
          let attorneyToSelect: Attorney | null = null
          if (user?.email) {
            const matchingAttorney = attorneysList.find(a =>
              a.email.toLowerCase() === user.email.toLowerCase()
            )
            if (matchingAttorney) attorneyToSelect = matchingAttorney
          }
          if (!attorneyToSelect) attorneyToSelect = attorneysList[0]
          setSelectedAttorney(attorneyToSelect.name)
        }
      } catch (error) {
        console.error('Failed to fetch attorneys:', error)
      } finally {
        setLoadingAttorneys(false)
      }
    }

    fetchAttorneys()
  }, [isOpen, user?.email])

  useEffect(() => {
    if (!isOpen || previewTouched) return
    const attorney = needsAttorney
      ? attorneys.find(a => a.name === selectedAttorney) ?? null
      : null
    if (needsAttorney && !attorney) return
    setPreviewMessage(buildMessage(selectedTemplate, attorney, lead.id))
  }, [selectedAttorney, selectedTemplate, lead.id, isOpen, attorneys, needsAttorney, previewTouched])

  const handleTemplateSelect = (key: SmsTemplateKey) => {
    setSelectedTemplate(key)
    setPreviewTouched(false)
  }

  const handleSendSms = async () => {
    if (!lead.phone) {
      onSmsSent(selectedTemplate, 'error', 'No phone number available for this lead')
      return
    }

    if (needsAttorney && !selectedAttorney) {
      onSmsSent(selectedTemplate, 'error', 'Please select an attorney')
      return
    }

    setSending(true)
    try {
      const result = await smsApi.sendSms({
        lead_id: lead.id,
        message_body: previewMessage
      })

      if (result.error === false) {
        onSmsSent(selectedTemplate, 'success', `SMS sent successfully to ${lead.name}`)
        onClose()
      } else {
        onSmsSent(selectedTemplate, 'error', result.message || "Failed to send SMS")
      }
    } catch (error) {
      console.error('Failed to send SMS:', error)
      onSmsSent(selectedTemplate, 'error', "Failed to send SMS. Please try again.")
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setSelectedAttorney("")
    setSelectedTemplate("UNANSWERED")
    setPreviewMessage("")
    setPreviewTouched(false)
    setSending(false)
    onClose()
  }

  if (!isOpen) return null

  const sendDisabled =
    sending ||
    !lead.phone ||
    !previewMessage.trim() ||
    (needsAttorney && (loadingAttorneys || attorneys.length === 0 || !selectedAttorney))

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="mx-auto w-[95vw] max-w-2xl transition-none p-4 sm:p-5 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send SMS to {lead.name}
          </DialogTitle>
          <DialogDescription>
            Choose a template and edit the message before sending
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {needsAttorney && (
            <div className="space-y-1">
              <Label className="text-sm font-medium text-slate-700">Select Attorney</Label>
              {loadingAttorneys ? (
                <div className="p-2 text-sm text-slate-500">Loading attorneys...</div>
              ) : attorneys.length === 0 ? (
                <div className="p-2 text-sm text-red-500">No attorneys available</div>
              ) : (
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {attorneys.map((attorney) => (
                    <div
                      key={attorney.name}
                      className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedAttorney === attorney.name
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => {
                        setSelectedAttorney(attorney.name)
                        setPreviewTouched(false)
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{attorney.name}</span>
                        {selectedAttorney === attorney.name && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Select Template</Label>
            <div className="space-y-1">
              {SMS_TEMPLATE_DEFINITIONS.map((t) => (
                <div
                  key={t.key}
                  className={`cursor-pointer rounded-lg border px-2.5 py-1.5 transition-colors ${
                    selectedTemplate === t.key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => handleTemplateSelect(t.key)}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-36 shrink-0 text-sm font-medium text-slate-900 sm:w-40">
                      {t.label}
                    </span>
                    <span className="min-w-0 flex-1 text-xs text-slate-600 sm:text-right">
                      {t.description}
                    </span>
                    {selectedTemplate === t.key ? (
                      <Check className="h-4 w-4 shrink-0 text-blue-600" />
                    ) : (
                      <span className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Message</Label>
            <Textarea
              value={previewMessage}
              onChange={(e) => {
                setPreviewTouched(true)
                setPreviewMessage(e.target.value)
              }}
              rows={6}
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendSms}
            disabled={sendDisabled}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {sending ? 'Sending...' : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
